import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import RichTextDisplay from 'Components/UI/RichTextDisplay';
import RichTextEditorCustomContextMenu from 'Components/UI/RichTextEditorCustomContextMenu';
import Typography from 'Components/UI/Typography';
import RoundedButton from 'Components/ComponentLibrary/Atoms/Inputs/RoundedButton';
import { DS_SIZE_MAP } from 'Components/ComponentLibrary/DesignSystem/sizes';

const styles = (theme) => ({
  root: {
    marginBottom: theme.spacing(2),
  },
  display: {
    padding: theme.spacing(1),
    margin: theme.spacing(0.5),
    '&:hover': {
      borderRadius: theme.spacing(1),
      borderWidth: theme.spacing(0.25),
      borderColor: theme.palette.facet.accentBlue,
      borderStyle: 'solid',
      margin: theme.spacing(0, 0),
      cursor: 'pointer',
    },
  },
  hidden: {
    display: 'none',
  },
  placeholder: {
    fontSize: 14,
    textAlign: 'center',
    padding: theme.spacing(1, 0),
  },
  actions: {
    padding: theme.spacing(1, 0),
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    marginLeft: theme.spacing(1),
    padding: theme.spacing(1),
  },
});

const RichTextDisplayEditor = ({
  className,
  classes,
  content,
  onSubmit,
  saving,
  placeholderLabel,
  RichTextEditorProps,
  EditorComponent,
  onEditEnter,
  onEditExit,
}) => {
  const [editMode, setEditMode] = React.useState(false);
  const [editorValue, setEditorValue] = React.useState(content);

  const enterEditMode = () => {
    setEditMode(true);
    if (onEditEnter) onEditEnter();
  };

  const exitEditMode = () => {
    setEditMode(false);
    if (onEditExit) onEditExit();
  };

  const handleCancel = async () => {
    setEditorValue(content);
    exitEditMode();
  };

  const handleSubmit = React.useCallback(async () => {
    await onSubmit(editorValue);
    exitEditMode();
  }, [editorValue]);

  const showPlaceholder = !editMode && !content;
  const hideEditor = !editMode;
  const hideDisplay = editMode || !content;

  return (
    <div className={classNames(classes.root, className)}>
      <RichTextDisplay
        className={classNames(classes.display, {
          [classes.hidden]: hideDisplay,
        })}
        content={content}
        onClick={enterEditMode}
      />
      <div
        className={classNames({
          [classes.hidden]: hideEditor,
        })}
      >
        <EditorComponent
          value={editorValue}
          onChange={(value) => {
            setEditorValue(value);
          }}
          {...RichTextEditorProps}
        />
        <div className={classes.actions}>
          <RoundedButton
            className={classes.button}
            disableElevation
            variant={'text'}
            size={DS_SIZE_MAP.sm}
            onClick={handleCancel}
          >
            {'Cancel'}
          </RoundedButton>
          <RoundedButton
            className={classes.button}
            size={DS_SIZE_MAP.sm}
            onClick={handleSubmit}
            isLoading={saving}
          >
            {'Submit'}
          </RoundedButton>
        </div>
      </div>
      {showPlaceholder && (
        <Typography className={classNames(classes.display, classes.placeholder)} color="darkGray" onClick={enterEditMode}>
          <i>{placeholderLabel}</i>
        </Typography>
      )}
    </div>
  );
};

RichTextDisplayEditor.defaultProps = {
  className: '',
  RichTextEditorProps: {},
  EditorComponent: RichTextEditorCustomContextMenu,
  onEditEnter: undefined,
  onEditExit: undefined,
  saving: false,
};

RichTextDisplayEditor.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  content: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  onEditEnter: PropTypes.func,
  onEditExit: PropTypes.func,
  placeholderLabel: PropTypes.string.isRequired,
  RichTextEditorProps: PropTypes.object,
  EditorComponent: PropTypes.object,
};

export default withStyles(styles)(RichTextDisplayEditor);
